import Data from "../application/Data";

export default class Utils {
    /**
     * The `copy` function creates a deep copy of the input data using JSON serialization and
     * deserialization.
     * @param {any} data - The `data` parameter in the `copy` function is of type `any`, which means it
     * can accept any data type. The function is designed to create a deep copy of the input data by
     * first converting it to a JSON string and then parsing it back to a new object. This ensures that
     * @returns The `copy` function is returning a deep copy of the `data` object by first converting
     * it to a JSON string and then parsing it back to create a new object.
     */
    static copy(data:any){
        return JSON.parse(JSON.stringify(data));
    }
    /**
     * The function `getDialogModel` returns a copy of a dialog model based on the provided ID.
     * @param {string} id - The `id` parameter is a string that represents the unique identifier of a
     * dialog model.
     * @returns The `getDialogModel` method is returning a copy of the dialog model with the specified
     * `id` from the `Data.data.config.dialogs` object.
     */
    static getDialogModel(id:string){
        return this.copy(Data.data.config.dialogs[id]);
    }
    /**
     * The function generates a random unique identifier (UID) of a specified length using a
     * combination of uppercase letters, lowercase letters, and numbers.
     * @param {number} [l=15] - The `l` parameter in the `generateUID` function is used to specify the
     * length of the generated UID. By default, if no length is provided when calling the function, it
     * will generate a UID of length 15 characters.
     * @returns The `generateUID` function returns a randomly generated string of length `l` (default
     * length is 15) consisting of uppercase letters, lowercase letters, and numbers.
     */
    static generateUID(l:number=15):string{
        //if(length) l = length;
        let uid:string = "";
        let chars:string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

        for (let i = 0; i < l; i++) {
            uid += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        return uid;
    }
    /**
     * The function `makeURLString` takes a string input and removes all non-alphanumeric characters
     * from it.
     * @param {string} str - The `makeURLString` function takes a string as input and removes any
     * characters that are not alphanumeric (letters or numbers).
     * @returns The `makeURLString` function takes a string as input and removes all non-alphanumeric
     * characters from it using a regular expression. The function then returns the modified string
     * with only alphanumeric characters.
     */
    static makeURLString(str:string){
        return str.replace(/[^a-zA-Z0-9]/g, '');
    }
    /**
     * The function `replace` takes a text, an expression to search for, and a replacement expression,
     * and returns the text with all instances of the expression replaced by the replacement
     * expression.
     * @param text - The `text` parameter is the original text or string where you want to replace a
     * specific expression with another expression.
     * @param expression - The `expression` parameter in the `replace` function is a string that
     * represents the pattern you want to search for in the `text` parameter. This pattern can be a
     * simple string or a regular expression.
     * @param replace_expression - The `replace_expression` parameter in the `replace` function is the
     * string that will replace the matched parts of the `text` based on the `expression`. It can be a
     * simple string or a pattern with placeholders to capture parts of the matched text.
     * @returns The `replace` function is being returned, which takes three parameters: `text`,
     * `expression`, and `replace_expression`. The function uses the `replace` method on the `text`
     * parameter with a regular expression created from the `expression` parameter and replaces it with
     * the `replace_expression`. The `g` flag in the regular expression ensures that all occurrences of
     * the `expression` are replaced in
     */
    static replace(text, expression, replace_expression) {
        return text.replace(new RegExp(expression, "g"), replace_expression);
    }
    /**
     * The function `getCountryFromLanguage` takes a language as input and returns the corresponding
     * country based on a predefined mapping.
     * @param {string} language - The `getCountryFromLanguage` function takes a `language` parameter as
     * input. This function searches through a data structure to find the country associated with the
     * given language. If the language is found in the data structure, the function returns the
     * corresponding country. If the language is not found, the function
     * @returns The function `getCountryFromLanguage` returns the country code based on the input
     * language. If the language is found in the `countriesLanguages` object in the `Data.data.config`
     * data structure, the function returns the corresponding country code. If the language is not
     * found in any country, the function returns `null`.
     */
    static getCountryFromLanguage(language:string){
        for (let str in Data.data.config.countriesLanguages){
            if (Data.data.config.countriesLanguages[str].indexOf(language) != -1) return str;
        }

        return null;
    }
    /**
     * The function `createFile` creates a File object with specified data, filename, and type based on
     * the file extension.
     * @param {any} data - The `data` parameter in the `createFile` function represents the content
     * that will be written to the file. It could be text, JSON data, or binary data depending on the
     * file type being created.
     * @param {string} filename - The `filename` parameter in the `createFile` function is a string
     * that represents the name of the file that will be created. It is used to determine the file
     * extension and file type for the new file.
     * @returns A File object is being returned with the specified data, filename, and file type based
     * on the file extension.
     */
    static createFile(data: any, filename: string): File{
        let extension: string = this.getFileExtension(filename);
        let type:string;

        if (extension == "txt") type = "text/plain";
        else if (extension == "json") type = "application/json";
        else if (extension == "jpg" || extension == "jpeg")
        type = "image/jpg"; else if (extension == "gif")
        type = "image/gif"; else if (extension == "png")
        type = "image/png";

        return new File([data], filename, { lastModified: new Date().getTime(), type: type });
    }
    /**
     * The function `getFileExtension` takes a filename as input and returns the file extension.
     * @param {string} filename - The `filename` parameter is a string that represents the name of a
     * file including its extension.
     * @returns The function `getFileExtension` returns the file extension of the given filename.
     */
    static getFileExtension(filename:string){
        return filename.split('.').pop();
    }
    /**
     * The function `convertDataURIToBinary` converts a data URI string to binary data.
     * @param {string} dataURI - The `dataURI` parameter in the `convertDataURIToBinary` function is a
     * string that represents a data URI. It typically consists of a MIME type and the data itself,
     * often in base64 encoding. The function extracts the base64 data from the data URI, decodes it,
     * @returns An Uint8Array containing the binary data decoded from the given data URI is being
     * returned.
     */
    static convertDataURIToBinary(dataURI:string){
        let BASE64_MARKER:string = ';base64,';
        let base64Index:number = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length;
        let base64:string = dataURI.substring(base64Index);
        let raw:any = window.atob(base64);
        let rawLength:number = raw.length;
        let array:any = new Uint8Array(new ArrayBuffer(rawLength));

        for (let i = 0; i < rawLength; i++) {
            array[i] = raw.charCodeAt(i);
        }

        return array;
    }
    static removeTransparentPixelsFromBase64(c:any) {
        var ctx = c.getContext('2d');
        var copy = document.createElement('canvas').getContext('2d'),
            pixels = ctx.getImageData(0, 0, c.width, c.height),
            l = pixels.data.length,
            i,
            bound = {
                top: null,
                left: null,
                right: null,
                bottom: null
            },
            x, y;



        // Iterate over every pixel to find the highest
        // and where it ends on every axis ()
        for (i = 0; i < l; i += 4) {
            if (pixels.data[i + 3] !== 0) {
                x = (i / 4) % c.width;
                y = ~~((i / 4) / c.width);

                if (bound.top === null) {
                    bound.top = y;
                }

                if (bound.left === null) {
                    bound.left = x;
                } else if (x < bound.left) {
                    bound.left = x;
                }

                if (bound.right === null) {
                    bound.right = x;
                } else if (bound.right < x) {
                    bound.right = x;
                }

                if (bound.bottom === null) {
                    bound.bottom = y;
                } else if (bound.bottom < y) {
                    bound.bottom = y;
                }
            }
        }

        // Calculate the height and width of the content
        var trimHeight = bound.bottom - bound.top,
            trimWidth = bound.right - bound.left,
            trimmed = ctx.getImageData(bound.left, bound.top, trimWidth, trimHeight);

        copy.canvas.width = trimWidth;
        copy.canvas.height = trimHeight;
        copy.putImageData(trimmed, 0, 0);

        // Return trimmed canvas
        return copy.canvas;
    }
}